import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { useToast } from "../components/ui/use-toast";
import {
  Save,
  X,
  Plus,
  Trash2,
  Clock,
  CalendarDays,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  get_operational_timings,
  save_operational_timings,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

// ── Constants ─────────────────────────────────────────────────────────────────
const WEEK_DAYS = [
  { id: 1, name: "Monday" },
  { id: 2, name: "Tuesday" },
  { id: 3, name: "Wednesday" },
  { id: 4, name: "Thursday" },
  { id: 5, name: "Friday" },
  { id: 6, name: "Saturday" },
  { id: 7, name: "Sunday" },
];

const uid = () => Math.random().toString(36).slice(2);

const makeSubSlots = (startStr, endStr, ctx = {}) => {
  const { bookingInterval = 30, personLimit = 8 } = ctx;
  if (!startStr || !endStr) return [];
  const [sh, sm] = startStr.split(":").map(Number);
  const [eh, em] = endStr.split(":").map(Number);
  let startMins = sh * 60 + sm;
  const endMins = eh * 60 + em + (eh < sh ? 24 * 60 : 0);
  const subs = [];
  while (startMins + bookingInterval <= endMins) {
    const s = `${String(Math.floor(startMins / 60) % 24).padStart(2, "0")}:${String(startMins % 60).padStart(2, "0")}`;
    const e = `${String(Math.floor((startMins + bookingInterval) / 60) % 24).padStart(2, "0")}:${String((startMins + bookingInterval) % 60).padStart(2, "0")}`;
    subs.push({
      _uid: uid(),
      start_time: s,
      end_time: e,
      per_day_maximum_particular: personLimit,
      online_booking_time_status: 1,
      start_stop_time_status: 0,
    });
    startMins += bookingInterval;
  }
  return subs;
};

const EMPTY_SUB = (ctx = {}) => ({
  _uid: uid(),
  start_time: "",
  end_time: "",
  per_day_maximum_particular: ctx.personLimit || 0,
  online_booking_time_status: 1,
  start_stop_time_status: 0,
});

const makeSlot = (prefill = {}, ctx = {}) => ({
  _uid: uid(),
  time_name: prefill.time_name || "",
  start_time: prefill.start_time || "",
  end_time: prefill.end_time || "",
  last_arrival_time: "",
  per_day_maximum: 0,
  online_booking_status: 1,
  start_stop_status: 0,
  sub_slots:
    prefill.start_time && prefill.end_time
      ? makeSubSlots(prefill.start_time, prefill.end_time, ctx)
      : [],
  _expanded: true,
  _from_prefill: !!prefill.time_name,
});

const EMPTY_SPECIAL = () => ({
  _uid: uid(),
  speacial_date: "",
  time_name: "",
  start_time: "",
  end_time: "",
  last_arrival_time: "",
  per_day_maximum: 0,
  online_booking_status: 1,
  start_stop_status: 0,
  sub_slots: [],
  _expanded: true,
});

const initDays = () =>
  WEEK_DAYS.map((d) => ({
    week_id: d.id,
    start_stop_status: 1,
    online_booking_status: 1,
    slots: [],
  }));

// ── Component ─────────────────────────────────────────────────────────────────
export default function OperationalTimings() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(initDays);
  const [specialHours, setSpecialHours] = useState([]);
  const [mealPrefill, setMealPrefill] = useState([]);
  const [slotCtx, setSlotCtx] = useState({
    bookingInterval: 30,
    personLimit: 8,
  });
  const [errors, setErrors] = useState({});

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    const fd = new FormData();
    try {
      const res = await server_post_data(get_operational_timings, fd);
      if (res.data.error) throw new Error(res.data.message);

      const {
        meal_prefill,
        booking_interval,
        person_limit,
        op_dates,
        op_hours,
        op_details,
        special_hours,
        sp_details,
      } = res.data.message;

      const ctx = {
        bookingInterval: booking_interval,
        personLimit: person_limit,
      };
      setSlotCtx(ctx);
      setMealPrefill(meal_prefill || []);

      const built = initDays().map((d) => {
        const dateRow = op_dates.find((r) => r.week_id === d.week_id);
        const slots = op_hours
          .filter((h) => h.week_id === d.week_id)
          .map((h) => ({
            ...h,
            _uid: uid(),
            _expanded: true,
            sub_slots: op_details
              .filter((det) => det.operational_time_id === h.primary_id)
              .map((det) => ({ ...det, _uid: uid() })),
          }));
        return dateRow
          ? {
              ...d,
              start_stop_status: dateRow.start_stop_status,
              online_booking_status: dateRow.online_booking_status,
              slots,
            }
          : { ...d, slots };
      });
      setDays(built);

      setSpecialHours(
        special_hours.map((s) => ({
          ...s,
          _uid: uid(),
          _expanded: true,
          sub_slots: sp_details
            .filter((d) => d.operational_time_id === s.primary_id)
            .map((d) => ({ ...d, _uid: uid() })),
        })),
      );
    } catch {
      toast({
        title: "Error",
        description: "Failed to load timings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  // ── Day helpers ───────────────────────────────────────────────────────────
  const toggleDay = (weekId) =>
    setDays((prev) =>
      prev.map((d) => {
        if (d.week_id !== weekId) return d;
        const nowOpen = d.start_stop_status !== 0;
        const slots =
          nowOpen && d.slots.length === 0
            ? mealPrefill.map((m) => makeSlot(m, slotCtx))
            : d.slots;
        return { ...d, start_stop_status: nowOpen ? 0 : 1, slots };
      }),
    );

  const addSlot = (weekId) =>
    setDays((prev) =>
      prev.map((d) =>
        d.week_id === weekId
          ? { ...d, slots: [...d.slots, makeSlot({}, slotCtx)] }
          : d,
      ),
    );

  const removeSlot = (weekId, sUid) =>
    setDays((prev) =>
      prev.map((d) =>
        d.week_id === weekId
          ? { ...d, slots: d.slots.filter((s) => s._uid !== sUid) }
          : d,
      ),
    );

  const updateSlot = (weekId, sUid, field, value) =>
    setDays((prev) =>
      prev.map((d) => {
        if (d.week_id !== weekId) return d;
        return {
          ...d,
          slots: d.slots.map((s) => {
            if (s._uid !== sUid) return s;
            const updated = { ...s, [field]: value };
            if (field === "start_time" || field === "end_time") {
              const st = field === "start_time" ? value : s.start_time;
              const et = field === "end_time" ? value : s.end_time;
              if (st && et) updated.sub_slots = makeSubSlots(st, et, slotCtx);
            }
            return updated;
          }),
        };
      }),
    );

  const updateSub = (weekId, sUid, subUid, field, value) =>
    setDays((prev) =>
      prev.map((d) =>
        d.week_id === weekId
          ? {
              ...d,
              slots: d.slots.map((s) =>
                s._uid === sUid
                  ? {
                      ...s,
                      sub_slots: s.sub_slots.map((x) =>
                        x._uid === subUid ? { ...x, [field]: value } : x,
                      ),
                    }
                  : s,
              ),
            }
          : d,
      ),
    );

  // ── Special helpers ───────────────────────────────────────────────────────
  const addSpecial = () => setSpecialHours((p) => [...p, EMPTY_SPECIAL()]);
  const removeSpecial = (sUid) =>
    setSpecialHours((p) => p.filter((s) => s._uid !== sUid));
  const updateSpecial = (sUid, f, v) =>
    setSpecialHours((p) =>
      p.map((s) => (s._uid === sUid ? { ...s, [f]: v } : s)),
    );
  const updateSpSub = (sUid, subUid, f, v) =>
    setSpecialHours((p) =>
      p.map((s) =>
        s._uid === sUid
          ? {
              ...s,
              sub_slots: s.sub_slots.map((x) =>
                x._uid === subUid ? { ...x, [f]: v } : x,
              ),
            }
          : s,
      ),
    );

  // ── Validate ──────────────────────────────────────────────────────────────
  const validate = useCallback(() => {
    const errs = {};
    days.forEach((d) => {
      if (d.start_stop_status === 0) {
        if (!d.slots.length)
          errs[`day_${d.week_id}`] = "At least 1 slot required";
        d.slots.forEach((s) => {
          if (!s.start_time) errs[`${d.week_id}_${s._uid}_st`] = "Required";
          if (!s.end_time) errs[`${d.week_id}_${s._uid}_et`] = "Required";
        });
      }
    });
    specialHours.forEach((s) => {
      if (!s.speacial_date) errs[`sp_${s._uid}_date`] = "Required";
      if (!s.start_time) errs[`sp_${s._uid}_st`] = "Required";
      if (!s.end_time) errs[`sp_${s._uid}_et`] = "Required";
    });
    setErrors(errs);
    return !Object.keys(errs).length;
  }, [days, specialHours]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!validate()) {
      toast({
        title: "Validation Error",
        description: "Fix errors before saving",
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    const strip = ({ _uid, _expanded, _from_prefill, ...rest }) => ({
      ...rest,
      sub_slots: (rest.sub_slots || []).map(({ _uid: u, ...x }) => x),
    });
    const fd = new FormData();
    fd.append(
      "days",
      JSON.stringify(
        days.map(({ slots, ...d }) => ({ ...d, slots: slots.map(strip) })),
      ),
    );
    fd.append("special_hours", JSON.stringify(specialHours.map(strip)));
    try {
      const res = await server_post_data(save_operational_timings, fd);
      if (res.data.error) throw new Error(res.data.message);
      toast({ title: "Success", description: "Timings saved successfully" });
      fetchData();
    } catch {
      toast({
        title: "Error",
        description: "Failed to save timings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [days, specialHours, validate, toast, fetchData]);

  // ── UI helpers ────────────────────────────────────────────────────────────
  const Toggle = ({ on, onToggle, size = "md" }) => {
    const w = size === "sm" ? "w-7 h-4" : "w-9 h-5";
    const k = size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5";
    const t =
      size === "sm"
        ? on
          ? "translate-x-3"
          : "translate-x-0.5"
        : on
          ? "translate-x-4"
          : "translate-x-1";
    return (
      <button
        type="button"
        onClick={onToggle}
        className={`relative inline-flex items-center rounded-full transition-colors focus:outline-none ${w} ${on ? "bg-primary" : "bg-muted-foreground/30"}`}
      >
        <span
          className={`inline-block rounded-full bg-white transition-transform ${k} ${t}`}
        />
      </button>
    );
  };

  const Err = ({ id }) =>
    errors[id] ? (
      <p className="text-xs text-destructive flex items-center gap-1 mt-0.5">
        <AlertCircle className="w-3 h-3" />
        {errors[id]}
      </p>
    ) : null;

  // SubSlotRow — no discount field
  const SubSlotRow = ({ sub, onUpdate }) => (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end bg-background border rounded p-2">
      <div className="space-y-1">
        <Label className="text-xs">Start</Label>
        <Input
          type="time"
          value={sub.start_time}
          disabled
          className="bg-muted cursor-not-allowed"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">End</Label>
        <Input
          type="time"
          value={sub.end_time}
          disabled
          className="bg-muted cursor-not-allowed"
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Max Pax</Label>
        <Input
          type="number"
          min={0}
          value={sub.per_day_maximum_particular}
          onChange={(e) =>
            onUpdate("per_day_maximum_particular", e.target.value)
          }
        />
      </div>
      <div className="flex items-center gap-1 pt-5">
        <Toggle
          size="sm"
          on={sub.online_booking_time_status === 0}
          onToggle={() =>
            onUpdate(
              "online_booking_time_status",
              sub.online_booking_time_status === 0 ? 1 : 0,
            )
          }
        />
        <span className="text-xs text-muted-foreground">Online</span>
      </div>
      <div className="flex items-center gap-1 pt-5">
        <Toggle
          size="sm"
          on={sub.start_stop_time_status === 0}
          onToggle={() =>
            onUpdate(
              "start_stop_time_status",
              sub.start_stop_time_status === 0 ? 1 : 0,
            )
          }
        />
        <span className="text-xs text-muted-foreground">Active</span>
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary" /> Operational Timings
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Set weekly operating hours and special date overrides
        </p>
        {mealPrefill.length > 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            💡 Opening a day will auto-fill slots from meal times (
            {mealPrefill.map((m) => m.time_name).join(", ")}) and generate
            sub-slots every {slotCtx.bookingInterval} min (default:{" "}
            {slotCtx.personLimit} pax)
          </p>
        )}
      </div>

      {/* ── Weekly Schedule ── */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold">Weekly Schedule</h4>
        {days.map((day) => {
          const isOpen = day.start_stop_status === 0;
          const dayName = WEEK_DAYS.find((w) => w.id === day.week_id)?.name;
          return (
            <Card key={day.week_id} className={!isOpen ? "opacity-70" : ""}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="font-semibold">{dayName}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {isOpen ? "Open" : "Closed"}
                    </span>
                    <Toggle
                      on={isOpen}
                      onToggle={() => toggleDay(day.week_id)}
                    />
                  </div>
                </CardTitle>
              </CardHeader>

              {isOpen && (
                <CardContent className="space-y-3">
                  <Err id={`day_${day.week_id}`} />
                  {day.slots.map((slot, idx) => (
                    <div
                      key={slot._uid}
                      className="border rounded-md bg-muted/20"
                    >
                      {/* Slot header */}
                      <div className="flex items-center justify-between px-3 py-2 border-b">
                        <span className="text-xs font-medium">
                          {slot.time_name || `Slot ${idx + 1}`}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              updateSlot(
                                day.week_id,
                                slot._uid,
                                "_expanded",
                                !slot._expanded,
                              )
                            }
                            className="text-muted-foreground hover:text-foreground"
                          >
                            {slot._expanded ? (
                              <ChevronUp className="w-3.5 h-3.5" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeSlot(day.week_id, slot._uid)}
                            className="text-destructive hover:opacity-70"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {slot._expanded && (
                        <div className="p-3 space-y-3">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Slot Name</Label>
                              <Input
                                placeholder="e.g. Lunch"
                                value={slot.time_name}
                                readOnly={slot._from_prefill}
                                disabled={slot._from_prefill}
                                onChange={(e) =>
                                  !slot._from_prefill &&
                                  updateSlot(
                                    day.week_id,
                                    slot._uid,
                                    "time_name",
                                    e.target.value,
                                  )
                                }
                                className={
                                  slot._from_prefill
                                    ? "bg-muted cursor-not-allowed"
                                    : ""
                                }
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">
                                Start Time {!slot._from_prefill && "*"}
                              </Label>
                              <Input
                                type="time"
                                value={slot.start_time}
                                readOnly={slot._from_prefill}
                                disabled={slot._from_prefill}
                                onChange={(e) =>
                                  !slot._from_prefill &&
                                  updateSlot(
                                    day.week_id,
                                    slot._uid,
                                    "start_time",
                                    e.target.value,
                                  )
                                }
                                className={`${errors[`${day.week_id}_${slot._uid}_st`] ? "border-destructive" : ""} ${slot._from_prefill ? "bg-muted cursor-not-allowed" : ""}`}
                              />
                              {!slot._from_prefill && (
                                <Err id={`${day.week_id}_${slot._uid}_st`} />
                              )}
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">
                                End Time {!slot._from_prefill && "*"}
                              </Label>
                              <Input
                                type="time"
                                value={slot.end_time}
                                readOnly={slot._from_prefill}
                                disabled={slot._from_prefill}
                                onChange={(e) =>
                                  !slot._from_prefill &&
                                  updateSlot(
                                    day.week_id,
                                    slot._uid,
                                    "end_time",
                                    e.target.value,
                                  )
                                }
                                className={`${errors[`${day.week_id}_${slot._uid}_et`] ? "border-destructive" : ""} ${slot._from_prefill ? "bg-muted cursor-not-allowed" : ""}`}
                              />
                              {!slot._from_prefill && (
                                <Err id={`${day.week_id}_${slot._uid}_et`} />
                              )}
                            </div>
                            
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
                            <div className="space-y-1">
                              <Label className="text-xs">Max Pax / Day</Label>
                              <Input
                                type="number"
                                min={0}
                                value={slot.per_day_maximum}
                                onChange={(e) =>
                                  updateSlot(
                                    day.week_id,
                                    slot._uid,
                                    "per_day_maximum",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                            <div className="flex items-center gap-2 pt-5">
                              <Toggle
                                on={slot.online_booking_status === 0}
                                onToggle={() =>
                                  updateSlot(
                                    day.week_id,
                                    slot._uid,
                                    "online_booking_status",
                                    slot.online_booking_status === 0 ? 1 : 0,
                                  )
                                }
                              />
                              <span className="text-xs text-muted-foreground">
                                Online Booking
                              </span>
                            </div>
                            <div className="flex items-center gap-2 pt-5">
                              <Toggle
                                on={slot.start_stop_status === 0}
                                onToggle={() =>
                                  updateSlot(
                                    day.week_id,
                                    slot._uid,
                                    "start_stop_status",
                                    slot.start_stop_status === 0 ? 1 : 0,
                                  )
                                }
                              />
                              <span className="text-xs text-muted-foreground">
                                Active
                              </span>
                            </div>
                          </div>

                          {slot.sub_slots.length > 0 && (
                            <div className="space-y-2 pl-2 border-l-2 border-primary/30">
                              <p className="text-xs font-medium text-muted-foreground">
                                Sub-slots
                              </p>
                              {slot.sub_slots.map((sub) => (
                                <SubSlotRow
                                  key={sub._uid}
                                  sub={sub}
                                  onUpdate={(f, v) =>
                                    updateSub(
                                      day.week_id,
                                      slot._uid,
                                      sub._uid,
                                      f,
                                      v,
                                    )
                                  }
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* ── Special Dates ── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-primary" /> Special Date
            Overrides
          </h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1 text-xs"
            onClick={addSpecial}
          >
            <Plus className="w-3.5 h-3.5" /> Add Special Date
          </Button>
        </div>

        {specialHours.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6 border rounded-md">
            No special dates added yet
          </p>
        )}

        {specialHours.map((sp, idx) => (
          <Card key={sp._uid}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between">
                <span>
                  Special Date {idx + 1}{" "}
                  {sp.speacial_date ? `— ${sp.speacial_date}` : ""}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      updateSpecial(sp._uid, "_expanded", !sp._expanded)
                    }
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {sp._expanded ? (
                      <ChevronUp className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => removeSpecial(sp._uid)}
                    className="text-destructive hover:opacity-70"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </CardTitle>
            </CardHeader>

            {sp._expanded && (
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Date *</Label>
                    <Input
                      type="date"
                      value={sp.speacial_date}
                      onChange={(e) =>
                        updateSpecial(sp._uid, "speacial_date", e.target.value)
                      }
                      className={
                        errors[`sp_${sp._uid}_date`] ? "border-destructive" : ""
                      }
                    />
                    <Err id={`sp_${sp._uid}_date`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Slot Name</Label>
                    <Input
                      placeholder="e.g. Holiday Dinner"
                      value={sp.time_name}
                      onChange={(e) =>
                        updateSpecial(sp._uid, "time_name", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Start Time *</Label>
                    <Input
                      type="time"
                      value={sp.start_time}
                      onChange={(e) =>
                        updateSpecial(sp._uid, "start_time", e.target.value)
                      }
                      className={
                        errors[`sp_${sp._uid}_st`] ? "border-destructive" : ""
                      }
                    />
                    <Err id={`sp_${sp._uid}_st`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">End Time *</Label>
                    <Input
                      type="time"
                      value={sp.end_time}
                      onChange={(e) =>
                        updateSpecial(sp._uid, "end_time", e.target.value)
                      }
                      className={
                        errors[`sp_${sp._uid}_et`] ? "border-destructive" : ""
                      }
                    />
                    <Err id={`sp_${sp._uid}_et`} />
                  </div>
                </div>

            

                {sp.sub_slots.length > 0 && (
                  <div className="space-y-2 pl-2 border-l-2 border-primary/30">
                    <p className="text-xs font-medium text-muted-foreground">
                      Sub-slots
                    </p>
                    {sp.sub_slots.map((sub) => (
                      <SubSlotRow
                        key={sub._uid}
                        sub={sub}
                        onUpdate={(f, v) =>
                          updateSpSub(sp._uid, sub._uid, f, v)
                        }
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t">
        <Button
          variant="outline"
          onClick={fetchData}
          disabled={loading}
          className="gap-2"
        >
          <X className="w-4 h-4" /> Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
        >
          {loading ? (
            "Saving..."
          ) : (
            <>
              <Save className="w-4 h-4" /> Save Timings
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
